import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ShoppingCart, Clock, Euro, Building2 } from "lucide-react";
import api from "../../lib/api";

/**
 * LinkedRfqsBadge — appears on QuoteDetailPage when the user has previously
 * spawned one or more marketplace RFQs from this quote (via the
 * OutsourceBanner CTA). Fetches `/api/quotes/{id}/linked-rfqs` and shows the
 * total offer count + the cheapest/fastest offer received as a compact
 * supplier-radar card. Clicking the row navigates to the marketplace
 * focused on that RFQ.
 *
 * Visibility: render nothing when no linked RFQs exist (typical case).
 */
export default function LinkedRfqsBadge({ quoteId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState({ rfqs: [], total_offers: 0 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!quoteId) return;
    let cancelled = false;
    api.get(`/quotes/${quoteId}/linked-rfqs`).then((r) => {
      if (cancelled) return;
      setData(r.data || { rfqs: [], total_offers: 0 });
      setLoaded(true);
    }).catch(() => { setLoaded(true); });
    return () => { cancelled = true; };
  }, [quoteId]);

  if (!loaded || !data.rfqs || data.rfqs.length === 0) return null;

  const headerTitle = data.total_offers > 0
    ? t("linkedRfqs.title", { count: data.total_offers, defaultValue: `${data.total_offers} ${data.total_offers === 1 ? "offerta" : "offerte"} dal marketplace` })
    : t("linkedRfqs.waiting", { defaultValue: "RFQ pubblicata, in attesa di offerte" });

  return (
    <div
      className="mb-6 border border-emerald-500/40 bg-gradient-to-r from-emerald-500/[0.06] to-emerald-500/[0.02] rounded-sm p-4"
      data-testid="linked-rfqs-badge"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-sm bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
          {data.total_offers > 0 ? (
            <CheckCircle2 size={16} className="text-emerald-300"/>
          ) : (
            <ShoppingCart size={16} className="text-emerald-300"/>
          )}
        </div>
        <div className="flex-1">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-300 mb-1">
            // MARKETPLACE_LOOP · {data.rfqs.length} RFQ
          </div>
          <div className="font-display font-semibold text-slate-100 text-base">
            {headerTitle}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {data.rfqs.slice(0, 3).map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => navigate(`/marketplace?rfq=${r.id}`)}
            className="w-full text-left px-3 py-2.5 rounded-sm bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-emerald-500/40 transition-colors flex items-center gap-3"
            data-testid={`linked-rfq-${r.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-200 font-medium truncate">{r.title}</div>
              <div className="text-[11px] text-slate-500 font-mono-data mt-0.5">
                {t(`linkedRfqs.status.${r.status}`, { defaultValue: r.status?.toUpperCase() || "OPEN" })}
                {" · "}
                {r.offers_count} {r.offers_count === 1 ? t("linkedRfqs.offer", { defaultValue: "offerta" }) : t("linkedRfqs.offers", { defaultValue: "offerte" })}
              </div>
            </div>
            {r.cheapest_offer && (
              <div className="flex items-center gap-3 text-[11px] font-mono-data text-emerald-200 shrink-0">
                <span className="flex items-center gap-1" title={t("linkedRfqs.cheapestOffer", { defaultValue: "Offerta più economica" })}>
                  <Euro size={11}/> {Number(r.cheapest_offer.price_eur).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
                {r.fastest_offer && (
                  <span className="flex items-center gap-1" title={t("linkedRfqs.fastestOffer", { defaultValue: "Lead-time minimo" })}>
                    <Clock size={11}/> {r.fastest_offer.lead_time_days}g
                  </span>
                )}
                <span className="flex items-center gap-1 max-w-[140px] truncate" title={r.cheapest_offer.workshop_name}>
                  <Building2 size={11}/> {r.cheapest_offer.workshop_name}
                </span>
              </div>
            )}
          </button>
        ))}
        {data.rfqs.length > 3 && (
          <div className="text-[11px] text-slate-500 text-center pt-1">
            … {t("linkedRfqs.more", { count: data.rfqs.length - 3, defaultValue: `e altre ${data.rfqs.length - 3} RFQ` })}
          </div>
        )}
      </div>
    </div>
  );
}
